export async function extractTextFromPDF(file: File): Promise<string> {
  const pdfjsLib = await import('pdfjs-dist').catch(() => {
    throw new Error('PDF parsing library not available');
  });

  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const loadingTask = pdfjsLib.getDocument({ data });
        const pdf = await loadingTask.promise;

        let fullText = '';
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          const pageText = textContent.items
            .map((item) => ('str' in item ? String(item.str) : ''))
            .join(' ');
          fullText += pageText + '\n';
        }

        const text = fullText.trim();
        if (!text) {
          reject(
            new Error(
              'No text found in PDF. The PDF may be scanned images. Try an image file for OCR.',
            ),
          );
          return;
        }
        resolve(text);
      } catch (error) {
        reject(
          new Error(
            `Failed to extract text from PDF: ${error instanceof Error ? error.message : String(error)}`,
          ),
        );
      }
    };

    reader.onerror = () => reject(new Error('Failed to read PDF file'));
    reader.readAsArrayBuffer(file);
  });
}

export async function extractTextFromImage(file: File): Promise<string> {
  const Tesseract = await import('tesseract.js').catch(() => {
    throw new Error('OCR library not available');
  });

  try {
    const { data } = await Tesseract.default.recognize(file, 'eng', {});
    const text = data.text.trim();
    if (!text) {
      throw new Error(
        'No text found in image. Ensure the image is clear and contains readable text.',
      );
    }
    return text;
  } catch (error) {
    throw new Error(
      `Failed to extract text from image: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

export function isPDFFile(file: File): boolean {
  return file.name.split('.').pop()?.toLowerCase() === 'pdf';
}

export function isImageFile(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
  return (
    (!!extension && imageExtensions.includes(extension)) ||
    file.type.startsWith('image/')
  );
}

export async function extractTextFromWord(file: File): Promise<string> {
  const mammoth = await import('mammoth').catch(() => {
    throw new Error('Word document parsing library not available');
  });

  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.default.extractRawText({ arrayBuffer });
    const text = result.value.trim();
    if (!text) {
      throw new Error(
        'No text found in Word document. The document may be empty or image-only.',
      );
    }
    return text;
  } catch (error) {
    throw new Error(
      `Failed to extract text from Word document: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

export function isWordFile(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension === 'docx' || extension === 'doc';
}
